import React, { useRef, useEffect } from 'react';

import styles from './BettingTabs.module.scss';
import Icon from '@/common/components/Icon';
import clsx from 'clsx';
import { useAppSelector } from '@/core/store/hooks';

interface BettingTabsProps {
  tabs: string[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  onToggleAllCollapse: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  isVideoVisible: boolean;
  isAllCollapsed?: boolean;
  /** 与顶栏数据板收起联动，修正吸顶偏移 */
  isDataBoardVisible?: boolean;
  /** 体育首页右侧栏嵌入：tabs 顶对齐，由外层 flex + 下方列表滚动 */
  embeddedInSidebar?: boolean;
}

/**
 * 投注标签页组件
 */
const BettingTabs: React.FC<BettingTabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  onToggleAllCollapse,
  // onRefresh,
  // isRefreshing,
  isVideoVisible,
  isAllCollapsed = false,
  isDataBoardVisible = true,
  embeddedInSidebar = false,
}) => {
  const screenBreakpoint = useAppSelector((state) => state.config.screenBreakpoint);
  const isMobile = screenBreakpoint === 'md';
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);
  const hasInitializedScrollRef = useRef(false);

  // 滚动到激活的标签
  useEffect(() => {
    if (activeTabRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const activeElement = activeTabRef.current;

      // 首次渲染且激活首个 tab 时，保持列表贴左，避免初始被误滚动后左侧裁切
      const firstTabElement = container.querySelector('button');
      const isFirstTabActive = firstTabElement === activeElement;
      if (!hasInitializedScrollRef.current && isFirstTabActive) {
        container.scrollTo({ left: 0, behavior: 'auto' });
        hasInitializedScrollRef.current = true;
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const activeRect = activeElement.getBoundingClientRect();

      const nextScrollLeft =
        activeRect.left -
        containerRect.left +
        container.scrollLeft -
        containerRect.width / 2 +
        activeRect.width / 2;

      const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
      const clampedScrollLeft = Math.min(Math.max(0, nextScrollLeft), maxScrollLeft);

      container.scrollTo({
        left: clampedScrollLeft,
        behavior: 'smooth',
      });
      hasInitializedScrollRef.current = true;
    }
  }, [activeTab]);

  return (
    <div
      className={clsx(
        styles.tabsContainer,
        isVideoVisible ? styles.video : '',
        !isMobile && !isDataBoardVisible && !isVideoVisible ? styles.dataBoardCollapsed : '',
        embeddedInSidebar && styles.embeddedInSidebar,
      )}
    >
      <div className={styles.tabsWrapper} ref={scrollContainerRef}>
        {tabs.map((tab) => (
          <button
            key={tab}
            ref={tab === activeTab ? activeTabRef : null}
            className={`${styles.tab} _tf[14] ${tab === activeTab ? styles.active : ''}`}
            onClick={() => onTabChange(tab)}
          >
            {tab}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-4px">
        {/* <div className={styles.expandButton} onClick={onRefresh}>
          <Icon
            src="/images/common/refresh.svg"
            size={'16px'}
            color={'var(--Text-Main-10)'}
            className={isRefreshing ? styles.refreshing : ''}
          />
        </div> */}
        <div className={styles.expandButton} onClick={onToggleAllCollapse}>
          <Icon
            src="/images/common/sportsDetails/DoubleArrowExpand.svg"
            size={'16px'}
            color={'var(--Text-800)'}
            className={clsx(styles.expandIcon, isAllCollapsed && styles.collapsed)}
          />
        </div>
      </div>
    </div>
  );
};

export default BettingTabs;
